/**
 * Education. Doc: docs/overall-plan/02-MANGO-PERSONAL/education
 * Wired to GET/POST /api/v1/education/courses and /certifications.
 */
import React, { useEffect, useState } from 'react';
import { educationApi, type EducationCourse, type EducationCertification } from '../services/api';

const Education: React.FC = () => {
  const [courses, setCourses] = useState<EducationCourse[]>([]);
  const [certifications, setCertifications] = useState<EducationCertification[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'courses' | 'certifications'>('courses');
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [certName, setCertName] = useState('');
  const [certIssuer, setCertIssuer] = useState('');
  const [certIssueDate, setCertIssueDate] = useState('');
  const [certExpiryDate, setCertExpiryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [coursesRes, certsRes] = await Promise.all([
        educationApi.getCourses(),
        educationApi.getCertifications(),
      ]);
      if (coursesRes?.success) setCourses(Array.isArray(coursesRes?.data) ? coursesRes.data : []);
      if (certsRes?.success) setCertifications(Array.isArray(certsRes?.data) ? certsRes.data : []);
      if (coursesRes?.message) setMessage(coursesRes.message);
      if (!coursesRes?.success) setError(coursesRes?.error || 'Failed to load');
      if (!certsRes?.success) setError(certsRes?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddCourse = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await educationApi.createCourse({ title: newTitle.trim() });
      if (res?.success) {
        setNewTitle('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed');
    } catch {
      setError('Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCertification = async () => {
    if (!certName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await educationApi.createCertification({
        name: certName.trim(),
        issuer: certIssuer.trim() || undefined,
        issueDate: certIssueDate || undefined,
        expiryDate: certExpiryDate || undefined,
      });
      if (res?.success) {
        setCertName('');
        setCertIssuer('');
        setCertIssueDate('');
        setCertExpiryDate('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed');
    } catch {
      setError('Failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>📚 Education</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Courses, certificates, and learning progress
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#667eea', cursor: 'pointer', fontSize: '14px' }}>Add {tab === 'courses' ? 'Course' : 'Certification'}</button>
            <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button type="button" onClick={() => setTab('courses')} style={{ padding: '8px 16px', background: tab === 'courses' ? '#667eea' : '#e2e8f0', color: tab === 'courses' ? 'white' : '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Courses</button>
        <button type="button" onClick={() => setTab('certifications')} style={{ padding: '8px 16px', background: tab === 'certifications' ? '#667eea' : '#e2e8f0', color: tab === 'certifications' ? 'white' : '#334155', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Certifications</button>
      </div>
      {showAdd && tab === 'courses' && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input type="text" placeholder="Course title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAddCourse} disabled={submitting || !newTitle.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Add'}</button>
            <button type="button" onClick={() => { setShowAdd(false); setNewTitle(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {showAdd && tab === 'certifications' && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input type="text" placeholder="Certification name" value={certName} onChange={(e) => setCertName(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="text" placeholder="Issuer (optional)" value={certIssuer} onChange={(e) => setCertIssuer(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="date" placeholder="Issue date" value={certIssueDate} onChange={(e) => setCertIssueDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="date" placeholder="Expiry date" value={certExpiryDate} onChange={(e) => setCertExpiryDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAddCertification} disabled={submitting || !certName.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Add'}</button>
            <button type="button" onClick={() => { setShowAdd(false); setCertName(''); setCertIssuer(''); setCertIssueDate(''); setCertExpiryDate(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && !error && tab === 'courses' && courses.length === 0 && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>}
      {tab === 'courses' && courses.length === 0 && !error && !showAdd && <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No courses yet. Add a course above.</div>}
      {tab === 'courses' && courses.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {courses.map((c) => (
            <li key={c.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <strong>{c.title}</strong>
              {c.provider && <span style={{ marginLeft: '8px', color: '#64748b' }}>- {c.provider}</span>}
              {c.progressPct != null && <span style={{ marginLeft: '8px', fontSize: '12px' }}>({c.progressPct}%)</span>}
            </li>
          ))}
        </ul>
      )}
      {tab === 'certifications' && certifications.length === 0 && !error && !showAdd && <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No certifications yet. Add a certification above.</div>}
      {tab === 'certifications' && certifications.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {certifications.map((cert) => (
            <li key={cert.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
              <strong>{cert.name}</strong>
              {cert.issuer && <span style={{ marginLeft: '8px', color: '#64748b' }}>- {cert.issuer}</span>}
              {cert.expiryDate && <span style={{ marginLeft: '8px', fontSize: '12px' }}>expires {new Date(cert.expiryDate).toLocaleDateString()}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Education;
